import { apiFetch } from '@crowdex/host-kit/renderer/http';
import type { PluginSettingPicker } from '@crowdex/plugin-contracts';
import { Button } from '@crowdex/ui/components/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@crowdex/ui/components/select';
import { Play } from 'lucide-react';
import { useEffect, useState } from 'react';

type Model = {
  id: string;
  name: string;
  provider: string;
  providerTitle: string;
  input: string[];
  output: string[];
};

/** The select value of an empty setting: the user's default (Radix has no empty value). */
const DEFAULT = '__default';
const SAMPLE = 'Hello! This is how this voice sounds.';
const message = (reason: unknown) => (reason instanceof Error ? reason.message : String(reason));

/** `<provider>/<model>` as the SDK's `modelChoice` reads it. */
function modelChoiceOf(value: string): { provider?: string; model: string } | null {
  if (!value) return null;
  const slash = value.indexOf('/');
  const provider = value.slice(0, slash);
  return slash > 0 && /^[a-z0-9-]+(\.[a-z0-9-]+)+$/.test(provider)
    ? { provider, model: value.slice(slash + 1) }
    : { model: value };
}

function inference<T>(operation: string, input: unknown): Promise<T> {
  return apiFetch<T>(`/api/providers/inference.v1/${operation}`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ input }),
  });
}

function PickerSelect({
  describedBy,
  disabled,
  id,
  label,
  onChange,
  options,
  value,
}: {
  describedBy: string | undefined;
  disabled: boolean;
  id: string;
  label: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  value: string;
}) {
  const known = value === '' || options.some((option) => option.value === value);
  return (
    <Select
      disabled={disabled}
      onValueChange={(next) => onChange(next === DEFAULT ? '' : next)}
      value={value || DEFAULT}
    >
      <SelectTrigger
        aria-describedby={describedBy}
        aria-label={label}
        className="w-56"
        id={id}
        size="sm"
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {options.map((option) => (
          <SelectItem key={option.value || DEFAULT} value={option.value || DEFAULT}>
            {option.label}
          </SelectItem>
        ))}
        {!known && <SelectItem value={value}>{value}</SelectItem>}
      </SelectContent>
    </Select>
  );
}

/** A model setting: the models of the user's providers that make (and take) what it says. */
export function ModelSettingPicker({
  busy,
  describedBy,
  id,
  label,
  picker,
  save,
  value,
}: {
  busy: boolean;
  describedBy: string | undefined;
  id: string;
  label: string;
  picker: Extract<PluginSettingPicker, { kind: 'model' }>;
  save: (value: string) => void;
  value: string;
}) {
  const [models, setModels] = useState<Model[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    apiFetch<{ models: Model[] }>('/api/models').then(
      (listing) => live && setModels(listing.models),
      (reason: unknown) => live && setError(message(reason)),
    );
    return () => {
      live = false;
    };
  }, []);
  const options = (models ?? [])
    .filter(
      (model) =>
        model.output.includes(picker.output) &&
        (picker.input ?? []).every((input) => model.input.includes(input)),
    )
    .map((model) => ({
      value: `${model.provider}/${model.id}`,
      label: `${model.name} · ${model.providerTitle}`,
    }));
  return (
    <div className="space-y-1">
      <PickerSelect
        describedBy={describedBy}
        disabled={busy || models === null}
        id={id}
        label={label}
        onChange={save}
        options={[
          {
            value: '',
            label: models === null ? 'Loading…' : 'Default (Settings › Models)',
          },
          ...options,
        ]}
        value={value}
      />
      {error !== null && <p className="text-caption text-destructive">{error}</p>}
    </div>
  );
}

/** A voice setting: the voices of the model its `model` setting names, with a sample to hear. */
export function VoiceSettingPicker({
  busy,
  describedBy,
  id,
  label,
  model,
  save,
  value,
}: {
  busy: boolean;
  describedBy: string | undefined;
  id: string;
  label: string;
  /** The value of the model setting it follows (`<provider>/<model>`, or empty). */
  model: string;
  save: (value: string) => void;
  value: string;
}) {
  const [voices, setVoices] = useState<{ id: string; name: string }[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [playing, setPlaying] = useState(false);
  useEffect(() => {
    let live = true;
    setVoices(null);
    inference<{ voices: { id: string; name: string }[] }>(
      'voices.list',
      modelChoiceOf(model) ?? {},
    ).then(
      (answer) => live && setVoices(answer.voices),
      (reason: unknown) => {
        if (!live) return;
        setVoices([]);
        setError(message(reason));
      },
    );
    return () => {
      live = false;
    };
  }, [model]);
  const listen = async () => {
    setPlaying(true);
    setError(null);
    try {
      const { audio, mimeType } = await inference<{ audio: string; mimeType: string }>('speech', {
        text: SAMPLE,
        ...(modelChoiceOf(model) ?? {}),
        ...(value ? { voice: value } : {}),
      });
      const player = new Audio(`data:${mimeType};base64,${audio}`);
      player.addEventListener('ended', () => setPlaying(false), { once: true });
      await player.play();
    } catch (reason) {
      setPlaying(false);
      setError(message(reason));
    }
  };
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2">
        <Button
          aria-label={`Listen to ${label}`}
          disabled={playing || voices === null}
          onClick={() => void listen()}
          size="sm"
          type="button"
          variant="outline"
        >
          <Play className="size-3.5" />
          Listen
        </Button>
        <PickerSelect
          describedBy={describedBy}
          disabled={busy || voices === null}
          id={id}
          label={label}
          onChange={save}
          options={[
            { value: '', label: voices === null ? 'Loading…' : 'Provider default' },
            // Saved by id: two voices may share a name.
            ...(voices ?? []).map((voice) => ({ value: voice.id, label: voice.name })),
          ]}
          value={value}
        />
      </div>
      {error !== null && <p className="text-caption text-destructive">{error}</p>}
    </div>
  );
}
